import { Button } from "@/components/ui/button";
import { TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Edit2, Trash2, Link, Eye } from "lucide-react";
import { Subscription } from "@/integrations/supabase/types/";
import { useCurrency } from "@/hooks/use-currency";
import type { Currency } from "@/lib/currency-conversion";

interface SubscriptionTableRowProps {
  subscription: Subscription;
  onEdit: (subscription: Subscription) => void;
  onView: (subscription: Subscription) => void;
  onDelete: (subscription: Subscription) => void;
  onGenerateLink: (subscription: Subscription) => void;
  onSendNotification: (subscription: Subscription, type: 'email' | 'sms' | 'both') => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    case "cancelled":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

export const SubscriptionTableRow = ({
  subscription,
  onEdit,
  onView,
  onDelete,
  onGenerateLink,
  onSendNotification,
}: SubscriptionTableRowProps) => {
  const { formatAmount } = useCurrency();
  
  // Format amount with original currency
  const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    MAD: "د.م."
  };
  
  const originalCurrency = (subscription.currency || "USD") as Currency;
  const symbol = currencySymbols[originalCurrency] || "$";
  const displayAmount = `${symbol}${Number(subscription.price).toFixed(2)}`;
  
  return (
    <>
      <TableCell className="font-medium">
        <div className="space-y-1">
          <p className="font-semibold">{subscription.plan_name}</p>
          {subscription.transaction_id && (
            <p className="text-xs text-muted-foreground">ID: {subscription.transaction_id}</p>
          )}
          {subscription.payment_type && (
            <Badge variant="outline" className="text-xs mt-1">
              {subscription.payment_type === "invoice" ? "Invoice Payment" : subscription.payment_type === "recurring" ? "Recurring" : "One-Time"}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <p className="font-medium">{subscription.client_name || "-"}</p>
          <p className="text-sm text-muted-foreground">{subscription.client_email || "-"}</p>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="secondary"
          className={`${getStatusColor(subscription.payment_status || "")} px-3 py-1 font-medium`}
        >
          {subscription.payment_status}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <p className="font-bold text-lg">{displayAmount}</p>
          <Badge variant="outline" className="text-xs">
            {originalCurrency}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {new Date(subscription.created_at || "").toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="hover:bg-muted">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onView(subscription)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onGenerateLink(subscription)}>
              <Link className="mr-2 h-4 w-4" />
              Payment Link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(subscription)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(subscription)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </>
  );
};